/**
 * MachineRowLabelCell.tsx
 * Sticky left label cell for one Machine Schedule grid row: machine code,
 * name, optional capacity strip — and, for a synthesized deactivated-machine
 * row (G2-10, audit #2), a status flag chip (Decommissioned / Out of Service)
 * with an explanatory tooltip so planners see why the greyed row is still
 * listed and know to relocate its bookings.
 *
 * Extracted from MachineScheduleGrid.tsx (600-line cap, CODING-STANDARDS).
 * Color tokens from theme.schedule.* — no hex literals.
 */

import React from 'react';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { MachineBookingCalendar, MachineStatus } from '../../../types/schedule';
import { CapacityStrip } from '../../../components/schedule/CapacityStrip';
import { machineUtilization, capacityBand } from '../../../utils/scheduleCapacity';
import { machineStatusLabels } from '../../../constants/statusOptions';
import { LEFT_COL_WIDTH } from './scheduleGridShared';
import type { MachineGridRow } from './machineScheduleRowGrouping';

interface MachineRowLabelCellProps {
  machine: MachineGridRow;
  /** This machine's bookings in the window (capacity strip input). */
  machineBookings: MachineBookingCalendar[];
  /** Day columns of the window (capacity strip input). */
  days: string[];
  showCapacity: boolean;
  /** One-line "code · name" label (compact rows, 2026-07 sticky-headers UX pass). */
  compact?: boolean;
}

export const MachineRowLabelCell: React.FC<MachineRowLabelCellProps> = ({
  machine,
  machineBookings,
  days,
  showCapacity,
  compact = false,
}) => {
  const theme = useTheme();
  const statusLabel = machine.inactiveStatus
    ? machineStatusLabels[machine.inactiveStatus as MachineStatus] ?? machine.inactiveStatus
    : null;
  return (
    <Box
      sx={{
        width: LEFT_COL_WIDTH,
        minWidth: LEFT_COL_WIDTH,
        flexShrink: 0,
        px: 1.5,
        borderRight: `1px solid ${theme.border.color}`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.paper,
        position: 'sticky',
        left: 0,
        // Above EVERYTHING that scrolls in the lane: booking bars (2) AND the
        // non-work veil bands (3) — either one sliding over this frozen cell
        // reads as a "transparent" machine column (2026-07-24 rounds 3+4).
        zIndex: 4,
      }}
    >
      <Tooltip
        title={
          statusLabel
            ? `${machine.name} — ${statusLabel}: this machine was deactivated but still has bookings. ` +
              'Move the work to another machine.'
            : machine.name
        }
        placement="right"
        enterDelay={statusLabel ? 0 : 500}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Typography variant="caption" fontWeight={600} noWrap sx={{ display: 'block', fontSize: '0.72rem' }}>
            {machine.code}
          </Typography>
          {/* Compact rows: name inline after the code ("code · name") instead of a 2nd line. */}
          {compact && machine.name !== machine.code && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ fontSize: '0.66rem', minWidth: 0, flexShrink: 1 }}
            >
              · {machine.name}
            </Typography>
          )}
          {/* G2-10: decommissioned-with-bookings flag */}
          {statusLabel && (
            <Chip
              label={statusLabel}
              size="small"
              sx={{
                height: 14,
                fontSize: '0.55rem',
                fontWeight: 700,
                bgcolor: theme.schedule.noActivity,
                color: '#fff',
                '& .MuiChip-label': { px: 0.6 },
              }}
            />
          )}
        </Box>
      </Tooltip>
      {!compact && (
        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.65rem', display: 'block' }}>
          {machine.name}
        </Typography>
      )}
      {showCapacity && (() => {
        const util = machineUtilization(machineBookings, days);
        return <CapacityStrip pct={util} band={capacityBand(util)} />;
      })()}
    </Box>
  );
};
